import { sha256Hex } from "./checksum";
import { blobStore } from "./blob-store";
import { pagesCollection, trackPageMutation, settleRow } from "./collections/pages-collection";
import { nextCaptureIndex } from "./page-index";
import { isBlankFrame, averageHash } from "./capture-heuristics";
import { appQuotaGuard, type QuotaGuard } from "./storage-quota-guard";
import type { Page } from "./page";

export function isUploadReady(page: { sha256: string }): boolean {
  return page.sha256.length === 64;
}

export type PageTable = {
  get(id: string): Page | undefined;
  insert(page: Page): void;
  update(id: string, recipe: (draft: Page) => void): void;
  delete(id: string): void;
  list(): Page[];
  /** Resolves once this row's most recent write has fully settled. See
   * addPageFromBlob's comment for why callers need this between writes. */
  settle(id: string): Promise<void>;
};

export type BlobPort = {
  get(key: string): Promise<Blob | undefined>;
  set(key: string, blob: Blob): Promise<void>;
  delete(key: string): Promise<void>;
};

export type CaptureAnalysis = { blank: boolean; captureHash: string };

/** `pageIndex` pins the new row to an existing slot instead of appending —
 * a retake must land back where the old page was, or page 2 of 5 comes
 * back as page 6. `replaceRemote` is carried through to the upload queue;
 * see the field's comment on Page. */
export type AddPageOptions = { pageIndex?: number; replaceRemote?: boolean };

export type PersistDeps = {
  pages: PageTable;
  blobs: BlobPort;
  hash: (blob: Blob) => Promise<string>;
  onReady?: () => void;
  analyze?: (blob: Blob) => Promise<CaptureAnalysis>;
  quotaGuard?: QuotaGuard;
};

/** 32x32 downsample is plenty for a variance/hash check and cheap to draw.
 * Runs in the same background pass as the checksum — never on the
 * capture-time critical path. Rejection (e.g. no OffscreenCanvas) is
 * swallowed by the caller's Promise.allSettled; analysis is best-effort. */
async function analyzeCapture(blob: Blob): Promise<CaptureAnalysis> {
  const bitmap = await createImageBitmap(blob);
  const size = 32;
  const canvas = new OffscreenCanvas(size, size);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.drawImage(bitmap, 0, 0, size, size);
  const { data } = ctx.getImageData(0, 0, size, size);
  return { blank: isBlankFrame(data), captureHash: averageHash(data) };
}

export const defaultPersistDeps: PersistDeps = {
  pages: {
    get: id => pagesCollection.get(id),
    insert: page => {
      trackPageMutation(page.id, pagesCollection.insert(page));
    },
    update: (id, recipe) => {
      trackPageMutation(id, pagesCollection.update(id, recipe));
    },
    delete: id => {
      pagesCollection.delete(id);
    },
    list: () => pagesCollection.toArray,
    settle: settleRow,
  },
  blobs: blobStore,
  hash: sha256Hex,
  analyze: analyzeCapture,
  quotaGuard: appQuotaGuard,
  onReady: () => {
    void import("./queue-store").then(m => {
      void m.getQueue()?.runOnce();
    });
  },
};

/** Inserting a page and then patching it again moments later (here, and
 * again from the upload queue's claim/status updates) trips a known
 * @tanstack/db bug: a second optimistic write to the same row key landing
 * before the first has settled throws "Query contributors with the same
 * row key are not congruent" from the live-query engine (TanStack/db#1767,
 * #1783; fixes pending in TanStack/db#1794, #1795, unmerged as of db
 * 0.8.7 / react-db 0.3.7 — the latest published versions) — and, unlike
 * the "console-only, harmless" assumption this comment used to make, the
 * write itself gets dropped: it never reaches storage and the row never
 * appears in the UI. persistAndHash and recoverUnhashedPages avoid this by
 * awaiting `settle()` between writes to the same id (see PageTable), which
 * waits on the previous write's transaction rather than guessing a delay.
 * frontend.tsx still suppresses the (now much rarer, and truly harmless
 * when it does fire) console warning via known-issues.ts. Revisit once
 * upstream ships a release with the linked fixes. */
export function addPageFromBlob(
  scriptId: string,
  blob: Blob,
  persistDeps: PersistDeps = defaultPersistDeps,
  options: AddPageOptions = {},
): string {
  const id = crypto.randomUUID();
  const pageIndex =
    options.pageIndex ??
    nextCaptureIndex(persistDeps.pages.list().filter(p => p.scriptId === scriptId).map(p => p.pageIndex));
  persistDeps.pages.insert({
    id,
    scriptId,
    pageIndex,
    blobKey: id,
    sha256: "",
    blank: false,
    captureHash: null,
    uploadStatus: "pending",
    attemptCount: 0,
    failReason: null,
    nextAttemptAt: null,
    driveFileId: null,
    uploadedAt: null,
    replaceRemote: options.replaceRemote ?? false,
  });
  void persistAndHash(id, blob, persistDeps);
  return id;
}

export async function persistAndHash(id: string, blob: Blob, deps: PersistDeps): Promise<void> {
  // Let the insert above fully settle before writing to this row again —
  // see addPageFromBlob's comment.
  await deps.pages.settle(id);

  const [hashSettled, idbSettled, analyzeSettled] = await Promise.allSettled([
    deps.hash(blob),
    deps.blobs.set(id, blob),
    deps.analyze ? deps.analyze(blob) : Promise.resolve(null),
  ]);

  if (!deps.pages.get(id)) {
    if (idbSettled.status === "fulfilled") await deps.blobs.delete(id);
    return;
  }

  if (idbSettled.status === "rejected") {
    deps.quotaGuard?.recordFailure();
    deps.pages.update(id, draft => {
      draft.uploadStatus = "failed";
      draft.failReason = "idb-write";
    });
    return;
  }
  deps.quotaGuard?.recordSuccess();

  if (hashSettled.status === "rejected") {
    deps.pages.update(id, draft => {
      draft.uploadStatus = "failed";
      draft.failReason = "checksum";
    });
    return;
  }

  const analysis = analyzeSettled.status === "fulfilled" ? analyzeSettled.value : null;
  deps.pages.update(id, draft => {
    draft.sha256 = hashSettled.value;
    draft.uploadStatus = "pending";
    draft.failReason = null;
    if (analysis) {
      draft.blank = analysis.blank;
      draft.captureHash = analysis.captureHash;
    }
  });
  // onReady triggers the upload queue's claim(), which writes to this same
  // row — let this write settle first, same reason as above.
  await deps.pages.settle(id);
  deps.onReady?.();
}

export async function recoverUnhashedPages(deps: PersistDeps): Promise<void> {
  for (const page of deps.pages.list().filter(p => p.sha256 === "")) {
    let blob: Blob | undefined;
    try {
      blob = await deps.blobs.get(page.blobKey);
    } catch {
      deps.pages.update(page.id, draft => {
        draft.uploadStatus = "failed";
        draft.failReason = "idb-write";
      });
      continue;
    }
    if (!blob) {
      deps.pages.delete(page.id);
      continue;
    }
    try {
      const sha256 = await deps.hash(blob);
      deps.pages.update(page.id, draft => {
        draft.sha256 = sha256;
        draft.uploadStatus = "pending";
        draft.failReason = null;
      });
      await deps.pages.settle(page.id);
      deps.onReady?.();
    } catch {
      deps.pages.update(page.id, draft => {
        draft.uploadStatus = "failed";
        draft.failReason = "checksum";
      });
    }
  }
}
